import type { SpanT } from '../types';

/**
 * Inserts frameIndex into the sorted, non-overlapping span array.
 * Returns the same reference if frameIndex is already covered (referential integrity).
 * Adjacent frames extend existing spans; a frame bridging two spans merges them.
 *
 * Examples:
 *   spans=[{1,10},{30,40}], frameIndex=20  → [{1,10},{20,20},{30,40}]
 *   spans=[{1,10},{30,40}], frameIndex=11  → [{1,11},{30,40}]
 *   spans=[{1,10},{12,20}], frameIndex=11  → [{1,20}]
 */
export function sortUpsertFrameIndexToSpan(spans: SpanT[], frameIndex: number): SpanT[] {
  const result: SpanT[] = [];
  let pending: SpanT = {
    start: frameIndex,
    end: frameIndex
  };
  let inserted = false;
  for (const span of spans) {
    if (inserted) {
      result.push(span);
      continue;
    }
    if (frameIndex >= span.start && frameIndex <= span.end) {
      return spans;
    }
    if (span.end < pending.start - 1) {
      // Span ends before pending with a gap — flush span and keep looking
      result.push(span);
    } else if (span.end + 1 === pending.start) {
      // Span is left-adjacent to pending — extend pending left to absorb span
      pending = {
        start: span.start,
        end: pending.end
      };
    } else if (pending.end + 1 === span.start) {
      // Pending is left-adjacent to span — extend pending right to absorb span
      pending = {
        start: pending.start,
        end: span.end
      };
    } else {
      // Gap between pending and span — pending is in its final position
      result.push(pending);
      result.push(span);
      inserted = true;
    }
  }
  if (!inserted) {
    result.push(pending);
  }
  return result;
}

/**
 * Inserts a span range into the sorted, non-overlapping span array in a single O(n) pass.
 * Merges the new span with any overlapping or adjacent existing spans.
 *
 * Examples:
 *   spans=[{1,5},{20,25}], newSpan={3,22}  → [{1,25}]
 *   spans=[{1,5},{10,15}], newSpan={7,8}   → [{1,5},{7,8},{10,15}]
 *   spans=[{1,5},{10,15}], newSpan={6,9}   → [{1,15}]
 */
export function upsertSpan(spans: SpanT[], newSpan: SpanT): SpanT[] {
  const result: SpanT[] = [];
  let merged: SpanT = {
    ...newSpan
  };
  let inserted = false;
  for (const span of spans) {
    if (inserted) {
      result.push(span);
      continue;
    }
    if (span.end < merged.start - 1) {
      // Existing span ends before new span with a gap — flush it
      result.push(span);
      continue;
    }
    if (span.start > merged.end + 1) {
      // Existing span starts after new span with a gap — flush merged, then flush existing
      result.push(merged);
      result.push(span);
      inserted = true;
      continue;
    }

    // Overlapping or adjacent — absorb into merged
    merged = {
      start: Math.min(merged.start, span.start),
      end: Math.max(merged.end, span.end)
    };
  }
  if (!inserted) {
    result.push(merged);
  }
  return result;
}